import React, { useState, useEffect } from 'react';
import { getEventsPage, getAllAlbums } from '../../api/requests';
import { Alert, Col, Row, Badge, Button, Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import "./events.css";
import EventPagination from "./EventPagination";

// Hilfsfunktionen für Datum
const monthNames = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
function getFirstSentence(text) {
    if (!text) return "";
    const match = text.match(/.*?[.!?](\s|$)/);
    return match ? match[0] : text;
}

// Neue Hilfsfunktionen für das neue Model
function getMonthKeyFromDateTime(dateTimeStr) {
    const d = new Date(dateTimeStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function getDayFromDateTime(dateTimeStr) {
    return new Date(dateTimeStr).getDate();
}
function getMonthNameFromDateTime(dateTimeStr) {
    const d = new Date(dateTimeStr);
    return monthNames[d.getMonth()];
}
function getYearFromDateTime(dateTimeStr) {
    return new Date(dateTimeStr).getFullYear();
}
function getShortTimeFromDateTime(dateTimeStr) {
    if (!dateTimeStr) return "";
    const d = new Date(dateTimeStr);
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function groupEventsByMonth(events) {
    // { "2025-08": [event, ...], ... }
    const grouped = {};
    events.forEach((ev) => {
        const dateTimeStr = ev?.start_datetime || ev?.end_datetime;
        if (!dateTimeStr) return;

        const key = getMonthKeyFromDateTime(dateTimeStr);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(ev);
    });
    return grouped;
}

const Events = ({ events: suppliedEvents, previewLimit }) => {
    const [events, setEvents] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [page, setPage] = useState(1);
    const [pageInfo, setPageInfo] = useState({ count: 0, next: null, previous: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const controlled = suppliedEvents !== undefined;

    useEffect(() => {
        let cancelled = false;
        getAllAlbums().then((data) => {
            if (!cancelled) setAlbums(Array.isArray(data) ? data : (data?.results || []));
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (controlled) return;
        let cancelled = false;
        setLoading(true);
        setError("");
        const params = previewLimit
            ? { page: 1, upcoming: true, ordering: 'start_datetime' }
            : { page };
        getEventsPage(params).then((result) => {
            if (cancelled) return;
            setEvents(result.results);
            setPageInfo(result);
        }).catch(() => {
            if (!cancelled) setError("Events konnten nicht geladen werden.");
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [page, controlled, previewLimit]);

    const availableEvents = controlled ? suppliedEvents : events;
    const visibleEvents = previewLimit ? availableEvents.slice(0, previewLimit) : availableEvents;
    const grouped = groupEventsByMonth(visibleEvents);

    // Album zu Event finden (optional, falls Verknüpfung existiert)
    const getAlbumForEvent = (eventId) => {
        // Beispiel: Album hat ein Feld event_id (anpassen falls anders)
        return albums.find(a => a.event_id === eventId);
    };

    return (
        <Container className="events">
            {loading && <p role="status">Events werden geladen …</p>}
            {error && <Alert variant="danger">{error}</Alert>}
            {!loading && !error && visibleEvents.length === 0 && <p>Keine Events in diesem Zeitraum.</p>}
            {!loading && !error && Object.keys(grouped).sort().map(monthKey => {
                const [year, month] = monthKey.split("-");
                const monthLabel = `${monthNames[parseInt(month, 10) - 1]} ${year}`;
                return (
                    <section key={monthKey} className="events__month">
                        <h3 className="events__monthTitle">
                            {monthLabel}
                        </h3>
                        <Row xs={1} md={1} lg={1} className="events__monthRow">
                            {grouped[monthKey].map(event => {
                                const album = getAlbumForEvent(event.id);
                                return (
                                    <Col key={event.id}>
                                        <div className="events__item">
                                            {/* Datum-Badge */}
                                            <div className="events__date">
                                                <div className="events__dateDay">{getDayFromDateTime(event.start_datetime)}</div>
                                                <div className="events__dateMonth">{getMonthNameFromDateTime(event.start_datetime)}</div>
                                            </div>
                                            {/* Card-Inhalt */}
                                            <div className="events__content">
                                                <div className="events__top">
                                                    <Link to={`/event/${event.id}`} className="events__title">
                                                        {event.event_name}
                                                    </Link>
                                                    <Badge bg="primary" className="events__badge">
                                                        {event.is_active ? "Aktiv" : "Inaktiv"}
                                                    </Badge>
                                                </div>
                                                <time
                                                    dateTime={event.start_datetime}
                                                    className="events__time"
                                                >
                                                    {getDayFromDateTime(event.start_datetime)}. {getMonthNameFromDateTime(event.start_datetime)} {getYearFromDateTime(event.start_datetime)} · {getShortTimeFromDateTime(event.start_datetime)} Uhr
                                                    {event.end_datetime && (
                                                        <>
                                                            {" - "}
                                                            {getDayFromDateTime(event.end_datetime)}. {getMonthNameFromDateTime(event.end_datetime)} {getYearFromDateTime(event.end_datetime)} · {getShortTimeFromDateTime(event.end_datetime)} Uhr
                                                        </>
                                                    )}
                                                </time>
                                                <div className="events__desc plain-text">
                                                    {getFirstSentence(event.event_description)}
                                                </div>
                                                <div className="events__slots">
                                                    {event.number_participants} Plätze frei
                                                </div>
                                                <div className="events__actions">
                                                    <Button variant="link" className="events__action" as={Link} to={`/event/${event.id}`}>
                                                        Details
                                                    </Button>
                                                    {album && (
                                                        <Button variant="link" className="events__action" as={Link} to={`/album/${album.id}`}>
                                                            Zum Album
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="link"
                                                        className="events__action"
                                                        onClick={() => {
                                                            // .ics-Download-Link generieren
                                                            const dtStart = event.start_datetime ? new Date(event.start_datetime).toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z") : "";
                                                            const dtEnd = event.end_datetime ? new Date(event.end_datetime).toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z") : "";
                                                            const ics = [
                                                                "BEGIN:VCALENDAR",
                                                                "VERSION:2.0",
                                                                "BEGIN:VEVENT",
                                                                `SUMMARY:${event.event_name}`,
                                                                `DESCRIPTION:${event.event_description}`,
                                                                event.start_datetime ? `DTSTART:${dtStart}` : "",
                                                                event.end_datetime ? `DTEND:${dtEnd}` : "",
                                                                "END:VEVENT",
                                                                "END:VCALENDAR"
                                                            ].filter(Boolean).join("\r\n");
                                                            const blob = new Blob([ics], { type: "text/calendar" });
                                                            const url = URL.createObjectURL(blob);
                                                            const a = document.createElement("a");
                                                            a.href = url;
                                                            a.download = `${event.event_name}.ics`;
                                                            document.body.appendChild(a);
                                                            a.click();
                                                            setTimeout(() => {
                                                                document.body.removeChild(a);
                                                                URL.revokeObjectURL(url);
                                                            }, 100);
                                                        }}
                                                    >
                                                        .ics speichern
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </section>
                );
            })}
            {previewLimit ? (
                <Button variant="link" as={Link} to="/kalender">Alle Events im Kalender ansehen</Button>
            ) : !controlled && <EventPagination page={page} {...pageInfo} loading={loading} onPageChange={setPage} />}
        </Container>
    );
};

export default Events;
